import pick from "ramda/src/pick";
import omit from "ramda/src/omit";
import into from "ramda/src/into";
import map from "ramda/src/map";
import compose from "ramda/src/compose";
import evolve from "ramda/src/evolve";
import assoc from "ramda/src/assoc";
import cond from "ramda/src/cond";
import T from "ramda/src/T";
import equals from "ramda/src/equals";
import identity from "ramda/src/identity";

import {
  renameKeys,
  parseFinancial,
  momentFormatter,
} from "../../../lib/utils";

/**
 * @typedef {'all' | 'service'} GPType
 * @typedef {'standard' | 'actual'} ValueType
 * @typedef {'' | '-' | number} ValueState
 *
 * @typedef {object} GPReport
 * @property {string} year
 * @property {string} month
 * @property {Record<ValueType, ValueState>} all
 * @property {Record<ValueType, ValueState>} service
 *
 * @typedef {object} PPSReport
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} revenue
 * @property {string[]} pm
 * @property {string} lob
 * @property {string} client
 * @property {GPReport[]} gpreport
 *
 * @typedef {object} GPSpreadsheet
 * @property {string} year
 * @property {string[]} months
 * @property {{
 *   id: string,
 *   bymonths: Omit<GPReport, 'year' | 'month'>[]
 * }[]} projects
 */

/** map from a report api response to local fields */
const PPS_REPORT_MAPPER = {
  project_id: "id",
  project_code: "code",
  project_name: "name",
  revenue: "revenue",
  project_managers: "pm",
  lob_name: "lob",
  client_name: "client",
  key_project_manager: "key_project_manager",
  gross_profit_by_months: "gpreport",
  gross_profit_approved_ratio: "gross_profit_approved_ratio",
  latest_approve_date: "latest_approve_date",
};

const GP_REPORT_MAPPER = {
  gross_profit_all: "all",
  gross_profit_service: "service",
};

/** @type {(x: string) => ValueState} */
export const toValueState = cond([
  [equals(""), identity],
  [equals("-"), identity],
  [T, parseFinancial],
]);

// prettier-ignore
/** @type {(gpreport: array) => GPReport[]} */
export const toGP = into([], compose(
  map(renameKeys(GP_REPORT_MAPPER)),
  map(gp => assoc('year', momentFormatter('YYYY')(gp.month), gp)),
  map(evolve({
    month: momentFormatter('MMM'),
    all: map(toValueState),
    service: map(toValueState),
  }))
))

// prettier-ignore
/** @type {(json: object) => PPSReport[]} */
export const toProjects = json => into([], compose(
  map(pick(Object.keys(PPS_REPORT_MAPPER))),
  map(renameKeys(PPS_REPORT_MAPPER)),
  map(evolve({ gpreport: toGP }))
), json.projectProfitReport)

/**
 * @param {PPSReport[]} ppsreport
 */
export const toSpreadsheets = (ppsreport) => {
  /** @type {GPSpreadsheet[]} */
  const spreadsheets = [];

  const findYearIndex = (y) => spreadsheets.findIndex(({ year }) => year === y);
  const findRecordIndex = (idx, id) =>
    spreadsheets[idx].projects.findIndex((p) => p.id === id);
  const hasMonthOnYear = (idx, m) => spreadsheets[idx].months.includes(m);

  ppsreport.forEach((project) => {
    const { id, gpreport } = project;

    gpreport.forEach((report) => {
      let yearIndex = findYearIndex(report.year);

      if (yearIndex === -1) {
        spreadsheets.push({ year: report.year, months: [], projects: [] });
        yearIndex = findYearIndex(report.year);
      }

      if (!hasMonthOnYear(yearIndex, report.month)) {
        spreadsheets[yearIndex].months.push(report.month);
      }

      let recordIndex = findRecordIndex(yearIndex, id);

      if (recordIndex === -1) {
        spreadsheets[yearIndex].projects.push({ id, bymonths: [] });
        recordIndex = findRecordIndex(yearIndex, id);
      }

      spreadsheets[yearIndex].projects[recordIndex].bymonths.push(
        omit(["year", "month"], report)
      );
    });
  });

  return spreadsheets;
};



// WEBPACK FOOTER //
// ./src/pages/report/project-profit-sheet/model.js